import { RejectedFileReason } from '@triumph/design-system';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useCallback, useEffect, useRef, useState } from 'react';

import { FileType } from '@/components/pages/Applicant/Applicant.types';
import { runWithConcurrency } from '@/components/shared/utils/runWithConcurrency';
import { FILE_TYPE_OPTION_GROUPS } from '@/components/shared/utils/utils';

import { Dialog } from '../Dialog';
import {
    FILE_TYPE_DROPDOWN_PANEL_CLASS,
    FileTypeDropdownPanelStyles,
} from '../FileTypeDropdownPanel';

export { FILE_TYPE_OPTIONS } from '@/components/shared/utils/utils';

import {
    Actions,
    DeleteButton,
    DropZone,
    DropZoneSubText,
    DropZoneText,
    ErrorText,
    Field,
    FieldLabel,
    FileList,
    FileName,
    FileRow,
    HiddenFileInput,
    ModalContainer,
    ReadonlyTitle,
    RequiredAsterisk,
    Spinner,
    Subtitle,
} from './FileUploadDialog.StyledComponents';

type RowStatus = 'pending' | 'uploading' | 'done' | 'failed' | 'invalid';

interface UploadRow {
    id: string;
    file: File;
    fileType: FileType | null;
    status: RowStatus;
    error: string | null;
}

export interface FileUploadDialogProps {
    id: string;
    header: string;
    visible: boolean;
    onClose: () => void;
    /** Uploads a single file. Called for each row when the user submits, up to MAX_PARALLEL_UPLOADS at once. */
    onUploadFile: (file: File, fileType: FileType | null) => Promise<void> | void;
    /** Fires after each submit or retry with that attempt's files. The dialog stays open while any failed. */
    onBatchComplete?: (result: { succeeded: File[]; failed: File[] }) => void;
    /** Fires once no file is left to upload. `totalCount` includes files from earlier attempts. */
    onAllUploaded?: (totalCount: number) => void;
    /** Optional grey panel echoing what's being attached to (e.g. condition description). */
    contextLabel?: string;
    /** Optional descriptive paragraph above the upload area. */
    subtitle?: string;
    /** Override the default Attach File label. */
    submitLabel?: string;
    /** Visible dropdown placeholder for the per-row document type selector. */
    fileTypePlaceholder?: string;
    dismissableMask?: boolean;
    /** Pre-populate files (e.g. from a drop). Runs the same validation as manual selection. */
    initialFiles?: File[];
    /** Fires after the dialog has finished closing and returned focus to its opener. */
    onExited?: () => void;
    'data-testid'?: string;
}

// Mirrors design-system's non-exported ProhibitedFormats (+dll); zip is intentionally not in this list.
const BLOCKED_EXTENSIONS = new Set([
    'bat', 'kix', 'ps1', 'hta', 'jse', 'reg', 'vbe', 'com', 'exe', 'cmd', 'vbs', 'js',
    'wsf', 'wsh', 'psc1', 'bash', 'sh', 'at', 'inf', 'ipa', 'osx', 'pif', 'run', 'apk',
    'app', 'bin', 'cab', 'command', 'cpl', 'csh', 'ex_', 'gadget', 'inf1', 'ins', 'inx',
    'isu', 'job', 'ksh', 'lnk', 'msc', 'msi', 'msp', 'mst', 'out', 'paf', 'prg', 'rgs',
    'scr', 'sct', 'shb', 'shs', 'u3p', 'vb', 'vbscript', 'workflow', 'ws', 'dll',
]);

// Rows flagged here become status 'invalid' below, which handleSubmit filters out before upload.
const isBlockedFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    return BLOCKED_EXTENSIONS.has(ext);
};

const MAX_PARALLEL_UPLOADS = 20;

let rowIdCounter = 0;
const makeRow = (file: File): UploadRow => ({
    id: `row-${++rowIdCounter}`,
    file,
    fileType: null,
    status: isBlockedFile(file) ? 'invalid' : 'pending',
    error: isBlockedFile(file) ? RejectedFileReason.ProhibitedFormat : null,
});

const STATUS_LABEL: Record<RowStatus, string> = {
    pending: 'Ready',
    uploading: 'Uploading…',
    done: 'Uploaded',
    failed: 'Failed',
    invalid: 'Invalid',
};

const STATUS_COLOR: Record<RowStatus, string> = {
    pending: '#6b7280',
    uploading: '#2563eb',
    done: '#15803d',
    failed: '#dc2626',
    invalid: '#dc2626',
};

export const FileUploadDialog = ({
    id,
    header,
    visible,
    onClose,
    onUploadFile,
    onBatchComplete,
    onAllUploaded,
    contextLabel,
    subtitle,
    submitLabel = 'Attach File',
    fileTypePlaceholder = 'Document type',
    dismissableMask = false,
    initialFiles,
    onExited,
    'data-testid': dataTestId,
}: FileUploadDialogProps) => {
    const [rows, setRows] = useState<UploadRow[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [batchError, setBatchError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const uploadedSinceOpenRef = useRef(0);

    const addFiles = useCallback((incoming: File[]) => {
        if (incoming.length === 0) return;
        setRows((prev) => [...prev, ...incoming.map(makeRow)]);
    }, []);

    useEffect(() => {
        if (visible) {
            setRows(initialFiles?.length ? initialFiles.map(makeRow) : []);
            setIsDragging(false);
            setIsSubmitting(false);
            setBatchError(null);
        }
    }, [visible, initialFiles]);

    useEffect(() => {
        if (visible) uploadedSinceOpenRef.current = 0;
    }, [visible]);

    const handleClose = () => {
        if (isSubmitting) return;
        onClose();
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        addFiles(Array.from(e.target.files ?? []));
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(Array.from(e.dataTransfer.files ?? []));
    };

    const updateRow = (rowId: string, patch: Partial<UploadRow>) => {
        setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));
    };

    const removeRow = (rowId: string) => {
        setRows((prev) => prev.filter((r) => r.id !== rowId));
    };

    const setRowFileType = (rowId: string, fileType: FileType | null) => {
        updateRow(rowId, { fileType });
    };

    const remainingRows = rows.filter((r) => r.status !== 'invalid' && r.status !== 'done');
    const hasValidFiles = remainingRows.length > 0;
    const canSubmit = hasValidFiles && !isSubmitting;

    const handleSubmit = async () => {
        if (!hasValidFiles) return;
        setIsSubmitting(true);
        setBatchError(null);

        const failedIds = new Set<string>();

        await runWithConcurrency(remainingRows, MAX_PARALLEL_UPLOADS, async (row) => {
            updateRow(row.id, { status: 'uploading', error: null });
            try {
                await onUploadFile(row.file, row.fileType);
                updateRow(row.id, { status: 'done', error: null });
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Upload failed.';
                updateRow(row.id, { status: 'failed', error: message });
                failedIds.add(row.id);
            }
        });

        const succeeded = remainingRows.filter((r) => !failedIds.has(r.id)).map((r) => r.file);
        const failed = remainingRows.filter((r) => failedIds.has(r.id)).map((r) => r.file);

        setIsSubmitting(false);
        uploadedSinceOpenRef.current += succeeded.length;
        onBatchComplete?.({ succeeded, failed });

        if (failed.length === 0) {
            onAllUploaded?.(uploadedSinceOpenRef.current);
            onClose();
        } else {
            setBatchError(
                succeeded.length === 0
                    ? 'Failed to upload. Please try again.'
                    : `${succeeded.length} uploaded, ${failed.length} failed. Retry the remaining files or remove them.`,
            );
        }
    };

    const isRetry = hasValidFiles && remainingRows.every((r) => r.status === 'failed');
    const baseLabel = isRetry ? 'Retry Failed Upload' : submitLabel;
    const submitButtonLabel = remainingRows.length > 1 ? `${baseLabel}s` : baseLabel;

    return (
        <Dialog
            id={id}
            header={header}
            visible={visible}
            onHide={handleClose}
            dismissableMask={dismissableMask}
            onExited={onExited}
            data-testid={dataTestId}
        >
            <FileTypeDropdownPanelStyles />
            <ModalContainer>
                {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
                {contextLabel ? <ReadonlyTitle>{contextLabel}</ReadonlyTitle> : null}

                <Field>
                    <FieldLabel>
                        Files<RequiredAsterisk>*</RequiredAsterisk>
                    </FieldLabel>
                    <DropZone
                        data-testid="file-upload-dropzone"
                        isDragging={isDragging}
                        hasError={Boolean(batchError)}
                        onClick={() => {
                            if (isSubmitting) return;
                            fileInputRef.current?.click();
                        }}
                        onDragOver={(e) => {
                            if (isSubmitting) return;
                            e.preventDefault();
                            setIsDragging(true);
                        }}
                        onDragLeave={() => setIsDragging(false)}
                        onDrop={(e) => {
                            if (isSubmitting) return;
                            handleDrop(e);
                        }}
                        style={isSubmitting ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    >
                        <DropZoneText>
                            Drag &amp; drop files, or{' '}
                            <span style={{ textDecoration: 'underline' }}>click to browse</span>
                        </DropZoneText>
                        <DropZoneSubText>All file types except executables and scripts</DropZoneSubText>
                    </DropZone>
                    <HiddenFileInput
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileInputChange}
                        data-testid="file-upload-input"
                    />
                    {batchError ? <ErrorText>{batchError}</ErrorText> : null}
                    {rows.length > 0 ? (
                        <FileList>
                            {rows.map((row) => (
                                <FileRow
                                    key={row.id}
                                    data-testid={`file-upload-row-${row.id}`}
                                    style={{
                                        gridTemplateColumns: '1fr 200px 110px 32px',
                                    }}
                                >
                                    <FileName title={row.file.name}>
                                        <a
                                            href={URL.createObjectURL(row.file)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: '#2563eb',
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                            }}
                                        >
                                            {row.file.name}
                                        </a>
                                        {row.error ? (
                                            <ErrorText style={{ marginTop: 2 }}>
                                                {row.error}
                                            </ErrorText>
                                        ) : null}
                                    </FileName>
                                    <Dropdown
                                        data-testid={`file-upload-type-${row.id}`}
                                        value={row.fileType}
                                        options={FILE_TYPE_OPTION_GROUPS}
                                        optionGroupLabel="label"
                                        optionGroupChildren="items"
                                        panelClassName={FILE_TYPE_DROPDOWN_PANEL_CLASS}
                                        onChange={(e) => setRowFileType(row.id, e.value)}
                                        placeholder={fileTypePlaceholder}
                                        filter
                                        filterInputAutoFocus
                                        filterPlaceholder="Search"
                                        showClear
                                        style={{ minWidth: 0, maxWidth: '100%' }}
                                        disabled={
                                            isSubmitting ||
                                            row.status === 'uploading' ||
                                            row.status === 'done' ||
                                            row.status === 'invalid'
                                        }
                                    />
                                    <span
                                        data-testid={`file-upload-status-${row.id}`}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 6,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: STATUS_COLOR[row.status],
                                        }}
                                    >
                                        {row.status === 'uploading' ? (
                                            <>
                                                <Spinner style={{ width: 14, height: 14, borderWidth: 2 }} />
                                                {STATUS_LABEL[row.status]}
                                            </>
                                        ) : (
                                            STATUS_LABEL[row.status] || null
                                        )}
                                    </span>
                                    <DeleteButton
                                        type="button"
                                        onClick={() => removeRow(row.id)}
                                        disabled={row.status === 'uploading'}
                                        aria-label={`Remove ${row.file.name}`}
                                    >
                                        🗑
                                    </DeleteButton>
                                </FileRow>
                            ))}
                        </FileList>
                    ) : null}
                </Field>

                <Actions>
                    <Button
                        label="Cancel"
                        severity="secondary"
                        outlined
                        onClick={handleClose}
                        disabled={isSubmitting}
                    />
                    <Button
                        label={submitButtonLabel}
                        icon={isRetry ? 'pi pi-refresh' : 'pi pi-upload'}
                        severity="info"
                        onClick={handleSubmit}
                        loading={isSubmitting}
                        disabled={!canSubmit}
                        data-testid="file-upload-confirm"
                    />
                </Actions>
            </ModalContainer>
        </Dialog>
    );
};
